/**
 * Avatar — initials-based avatar with optional photo override.
 *
 * Used in: sidebar (logged-in user), topbar, team page, task page,
 * comments, etc.
 *
 * Sizing matches the prototype's avi sizes (sm, md, lg, xl).
 *
 * Color derivation: a stable hash of the name picks one of 8 preset
 * tones. Same name => same colour every time. Matches the prototype's
 * `tone={(i % 7) + 1}` pattern but auto-derived rather than passed in.
 */

const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-7 h-7 text-[11px]',
  md: 'w-9 h-9 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-[88px] h-[88px] text-[30px]',
};

// 8 muted background tones with high-contrast text colour
const TONES = [
  { bg: '#FEE2E2', fg: '#991B1B' }, // red
  { bg: '#FED7AA', fg: '#9A3412' }, // orange
  { bg: '#FEF3C7', fg: '#92400E' }, // amber
  { bg: '#D1FAE5', fg: '#065F46' }, // emerald
  { bg: '#CFFAFE', fg: '#155E75' }, // cyan
  { bg: '#DBEAFE', fg: '#1E40AF' }, // blue
  { bg: '#E0E7FF', fg: '#3730A3' }, // indigo
  { bg: '#FCE7F3', fg: '#9D174D' }, // pink
];

function getInitials(name) {
  if (!name || typeof name !== 'string') return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pickTone(name) {
  if (!name) return TONES[0];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return TONES[Math.abs(h) % TONES.length];
}

export default function Avatar({
  name = '',
  photoUrl,
  size = 'md',
  className = '',
  title,
}) {
  const initials = getInitials(name);
  const tone = pickTone(name || initials);
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || 'User avatar'}
        title={title || name}
        className={`${sizeCls} rounded-full object-cover border border-border-base ${className}`}
      />
    );
  }

  return (
    <span
      title={title || name}
      aria-label={name || 'User avatar'}
      className={`${sizeCls} inline-grid place-items-center rounded-full font-semibold select-none ${className}`}
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {initials}
    </span>
  );
}